import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import i18next from 'i18next';
import { Text } from '@famly/mf_data-display_text';
import { Box } from '@famly/mf_layout_box';
import { Stack } from '@famly/mf_layout_stack';
import { useBreakpoints } from '@famly/mf_util_breakpoints';
import { Icon } from '@famly/mf_data-display_icon';
import { exhaustiveCheck } from '@famly/stat_ts-utils_exhaustive-check';
import { useDataProps } from '@famly/mf_ts-utils_data-aria-props';
import { Button } from '@famly/mf_input_button';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { BannerTestAttributes as TestAttributes } from './banner-test-attributes';
export const Banner = (props) => {
    const { text, severity = 'neutral', icon, button, link, onDismiss } = props;
    const { isMobileLandscapeAndLarger: shouldUseWideLayout } = useBreakpoints();
    const dataProps = useDataProps(props);
    const defaultIcon = getDefaultIcon(severity);
    const hasButton = hasValue(button);
    const hasLink = hasValue(link);
    const shouldRenderSecondRow = !shouldUseWideLayout && (hasButton || hasLink);
    const isWideButtonLayout = hasButton && shouldUseWideLayout;
    return (_jsxs(BannerContainer, { severity: severity, shouldUseWideLayout: shouldUseWideLayout, isWideButtonLayout: isWideButtonLayout, ...dataProps, children: [_jsxs(Stack, { direction: "row", alignItems: "flex-start", children: [_jsx(Stack, { height: isWideButtonLayout ? minHeightWithButton : minHeightWithoutButton, alignItems: "center", children: _jsx(Icon, { name: icon !== null && icon !== void 0 ? icon : defaultIcon, size: iconSize, color: "n500" }) }), _jsx(Text, { variant: "body", marginLeft: 2, marginRight: "auto", marginTop: isWideButtonLayout ? 5 : 3.5, marginBottom: shouldRenderSecondRow ? 2 : 3.5, "aria-role": severity === 'error' ? 'alert' : 'status', children: text }), shouldUseWideLayout && hasLink ? (_jsx(Stack, { alignItems: "center", height: isWideButtonLayout ? minHeightWithButton : minHeightWithoutButton, marginLeft: 6, flexShrink: 0, children: _jsx(BannerLink, { href: link.href, target: link.target, children: _jsx(LinkContents, { ...link }) }) })) : null, shouldUseWideLayout && hasButton ? (_jsx(Stack, { alignItems: "center", height: minHeightWithButton, marginLeft: 6, flexShrink: 0, children: _jsx(Button, { "data-e2e-id": TestAttributes.action, text: button.text, onClick: button.onClick, size: "compact", variant: "tertiary", isLoading: button.isLoading, icon: button.icon }) })) : null, hasValue(onDismiss) ? (_jsx(Stack, { alignItems: "center", height: isWideButtonLayout ? minHeightWithButton : minHeightWithoutButton, marginLeft: shouldUseWideLayout ? 2 : 4, children: _jsx(IconButton, { "aria-label": i18next.t($ => $.mf.dataDisplay_banner.src.translations.json.closeAriaLabel), size: "small", "data-e2e-id": TestAttributes.close, onClick: onDismiss, children: _jsx(Icon, { name: "close", color: "n400" }) }) })) : null] }), shouldRenderSecondRow ? (_jsxs(Box, { paddingBottom: 4, paddingLeft: `${iconSize}px`, children: [hasLink ? (_jsx(BannerLink, { href: link.href, marginLeft: 2, children: _jsx(LinkContents, { ...link }) })) : null, hasButton ? (_jsx(Box, { marginLeft: 2, children: _jsx(Button, { "data-e2e-id": TestAttributes.action, text: button.text, onClick: button.onClick, size: "compact", variant: "tertiary", isLoading: button.isLoading, icon: button.icon }) })) : null] })) : null] }));
};
const LinkContents = (props) => {
    return (_jsxs(_Fragment, { children: [_jsx("span", { children: props.text }), props.target === '_blank' ? _jsx(Icon, { name: "north_east", size: 14 }) : null] }));
};
const getBackgroundColor = (severity) => {
    switch (severity) {
        case 'neutral':
            return 'n50';
        case 'info':
            return 'b100';
        case 'upsell':
            return 'o100';
        case 'warning':
            return 'y100';
        case 'error':
            return 'r100';
        default:
            exhaustiveCheck(severity);
            return 'b100';
    }
};
const getDefaultIcon = (severity) => {
    switch (severity) {
        case 'neutral':
            return 'notifications_active';
        case 'info':
            return 'info';
        case 'upsell':
            return 'notifications_active';
        case 'warning':
            return 'warning';
        case 'error':
            return 'emergency_home';
        default:
            exhaustiveCheck(severity);
            return 'notifications_active';
    }
};
const iconSize = 18;
const minHeightWithButton = '60px';
const minHeightWithoutButton = '48px';
const BannerContainer = styled('div')(({ theme, severity, isWideButtonLayout }) => ({
    paddingRight: theme.modernFamlyTheme.spacing(3),
    paddingLeft: theme.modernFamlyTheme.spacing(4),
    minHeight: isWideButtonLayout ? minHeightWithButton : minHeightWithoutButton,
    boxShadow: theme.modernFamlyTheme.elevation[1],
    backgroundColor: theme.modernFamlyTheme.colorPalette[getBackgroundColor(severity)],
}));
const BannerLink = styled(Link)(({ theme }) => ({
    textDecorationColor: 'unset',
    fontSize: theme.typography['body-small'].fontSize,
    color: theme.modernFamlyTheme.colorPalette.p400,
    display: 'flex',
    alignItems: 'center',
    gap: theme.modernFamlyTheme.spacing(0.5),
}));
